import { createContext, ReactNode, useContext, useId } from 'react'
import clsx from 'clsx'
import { useTranslation } from 'react-i18next'
import { IconName } from '@fortawesome/fontawesome-svg-core'
import { Icon } from 'components/ui/Icon'

type ModalContextValue = {
  titleId: string
  onClose: () => void
}

const ModalContext = createContext<ModalContextValue | null>(null)

const useModalContext = () => {
  const ctx = useContext(ModalContext)
  if (!ctx) throw new Error('Modal.* subcomponents must be used inside <Modal>')
  return ctx
}

const ICON_BUTTON_CLASSES =
  'tw-flex tw-h-8 tw-w-8 tw-shrink-0 tw-items-center tw-justify-center tw-rounded-full tw-border-0 tw-bg-transparent tw-cursor-pointer tw-text-[var(--text-tertiary)] tw-transition-colors hover:tw-bg-[var(--input-bg)] hover:tw-text-[var(--text-primary)]'

export type ModalProps = {
  /** Dismiss handler shared with the header's close control. */
  onClose: () => void
  children: ReactNode
  /** Extra classes for the dialog root. */
  className?: string
}

/**
 * Dialog shell: owns padding, dialog semantics, and the close/title wiring
 * shared with its subcomponents. Compose with ModalHeader / ModalBody / ModalFooter.
 */
export const Modal = ({ onClose, children, className }: ModalProps) => {
  const titleId = useId()
  return (
    <ModalContext.Provider value={{ titleId, onClose }}>
      <section
        role="dialog"
        aria-modal="true"
        aria-labelledby={titleId}
        className={clsx('tw-flex tw-flex-col tw-p-5', className)}
      >
        {children}
      </section>
    </ModalContext.Provider>
  )
}

export type ModalHeaderProps = {
  title: string
  /** Brand-tinted glyph shown before the title. */
  icon?: IconName
  /** Renders a back affordance before the icon/title. */
  onBack?: () => void
}

export const ModalHeader = ({ title, icon, onBack }: ModalHeaderProps) => {
  const { t } = useTranslation()
  const { titleId, onClose } = useModalContext()

  return (
    <header className="tw-mb-5 tw-flex tw-items-center tw-gap-3">
      {onBack && (
        <button type="button" aria-label={t('goBack')} onClick={onBack} className={ICON_BUTTON_CLASSES}>
          <Icon aria-hidden icon={['far', 'arrow-left']} />
        </button>
      )}

      {icon && (
        <span
          aria-hidden
          className="tw-flex tw-h-10 tw-w-10 tw-shrink-0 tw-items-center tw-justify-center tw-rounded-xl"
          style={{
            backgroundColor: 'color-mix(in srgb, var(--brand-text) 12%, transparent)',
            color: 'var(--brand-text)',
          }}
        >
          <Icon icon={['far', icon]} className="tw-h-[18px] tw-w-[18px]" />
        </span>
      )}

      <h2
        id={titleId}
        className="tw-line-clamp-2 tw-min-w-0 tw-flex-1 tw-text-lg tw-font-bold tw-leading-tight"
        style={{ color: 'var(--text-primary)' }}
      >
        {title}
      </h2>

      <button type="button" aria-label={t('close')} onClick={onClose} className={ICON_BUTTON_CLASSES}>
        <Icon aria-hidden icon={['far', 'times']} />
      </button>
    </header>
  )
}

export type ModalBodyProps = {
  children: ReactNode
  className?: string
}

export const ModalBody = ({ children, className }: ModalBodyProps) => (
  <div className={clsx('tw-flex tw-flex-col', className)}>{children}</div>
)

export type ModalFooterProps = {
  children: ReactNode
  className?: string
}

export const ModalFooter = ({ children, className }: ModalFooterProps) => (
  <div className={clsx('tw-mt-6 tw-w-full', className)}>{children}</div>
)
